import { Package, Ship, Boxes, Tags, Apple } from "lucide-react";
import Reveal from "@/components/Reveal";

const WAREHOUSE_IMG =
  "https://images.unsplash.com/photo-1689942010216-dc412bb1e7a9?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";
const CRANE_IMG =
  "https://images.unsplash.com/photo-1597334948330-38795f25d05d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

const CARDS = [
  {
    id: "wholesale-merchandise",
    index: "01",
    icon: Package,
    title: "Wholesale Merchandise",
    desc: "General merchandise across a wide range of categories, sourced to your product mix and the quantities you need.",
    wide: true,
  },
  {
    id: "import-export",
    index: "02",
    icon: Ship,
    title: "Import & Export",
    desc: "Domestic and international moves coordinated end to end — sourcing, logistics, and delivery to your destination.",
    image: CRANE_IMG,
    imageAlt: "Crane lifting a shipping container at a port terminal",
  },
  {
    id: "overstock-goods",
    index: "03",
    icon: Boxes,
    title: "Overstock Goods",
    desc: "Clean surplus inventory from retailers and manufacturers moving excess stock.",
  },
  {
    id: "shelf-pulls",
    index: "04",
    icon: Tags,
    title: "Shelf Pulls",
    desc: "Store-clearance merchandise pulled from retail shelves, priced by the lot.",
  },
  {
    id: "perishables",
    index: "05",
    icon: Apple,
    title: "Perishables",
    desc: "Food and perishable lots handled on tight timelines, with conditions agreed per order.",
  },
];

const CardShell = ({ card, children }) => (
  <div
    data-testid={`service-card-${card.id}`}
    className={`group relative h-full rounded-2xl overflow-hidden transition-colors duration-300 border ${
      card.wide || card.image ? "border-transparent" : "border-slate-200 bg-surface hover:border-slate-300"
    }`}
  >
    {children}
  </div>
);

const CardBody = ({ card, light }) => (
  <>
    <div className="relative z-10 h-full flex flex-col p-7 md:p-8">
      <div className="flex items-start justify-between">
        <span
          className={`font-mono text-[11px] tracking-[0.3em] ${light ? "text-white/70" : "text-slate-400"}`}
        >
          {card.index}
        </span>
        <card.icon size={22} strokeWidth={1.8} className={light ? "text-white" : "text-accent"} />
      </div>
      <h3
        className={`mt-16 md:mt-20 font-display font-bold tracking-tight text-[22px] md:text-[24px] ${
          light ? "text-white" : "text-ink"
        }`}
      >
        {card.title}
      </h3>
      <p className={`mt-3 text-[14.5px] leading-relaxed ${light ? "text-white/80" : "text-slate-600"}`}>
        {card.desc}
      </p>
      <p
        className={`mt-auto pt-6 font-mono text-[10px] tracking-[0.22em] uppercase ${
          light ? "text-white/60" : "text-slate-400"
        }`}
      >
        Call for availability & pricing
      </p>
    </div>
  </>
);

const Services = () => (
  <section id="services" className="py-24 md:py-32 bg-surface border-y border-slate-200" data-testid="services-section">
    <div className="max-w-[1240px] mx-auto px-5 md:px-8">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-accent" data-testid="services-eyebrow">
              Products & Services
            </p>
            <h2
              className="mt-5 font-display font-extrabold text-ink tracking-[-0.02em] leading-[1.02] text-[clamp(2rem,4.2vw,3.4rem)] max-w-xl"
              data-testid="services-heading"
            >
              What we source and move.
            </h2>
          </div>
          <p className="text-[15px] text-slate-500 max-w-sm" data-testid="services-note">
            Categories and quantities change weekly. Every listing is a conversation —
            call for availability and pricing.
          </p>
        </div>
      </Reveal>

      <div className="mt-14 grid gap-4 md:grid-cols-6">
        {/* Row 1 */}
        <Reveal className="md:col-span-4" delay={0.05}>
          <CardShell card={{ ...CARDS[0], image: WAREHOUSE_IMG }}>
            <img
              src={WAREHOUSE_IMG}
              alt="Warehouse racks filled with wholesale merchandise and pallets"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
              data-testid="service-image-wholesale-merchandise"
            />
            <div className="absolute inset-0 bg-ink/60 transition-colors duration-300 group-hover:bg-ink/50" aria-hidden="true" />
            <CardBody card={{ ...CARDS[0] }} light />
          </CardShell>
        </Reveal>
        <Reveal className="md:col-span-2" delay={0.1}>
          <CardShell card={CARDS[1]}>
            <img
              src={CARDS[1].image}
              alt={CARDS[1].imageAlt}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
              data-testid="service-image-import-export"
            />
            <div className="absolute inset-0 bg-ink/60 transition-colors duration-300 group-hover:bg-ink/50" aria-hidden="true" />
            <CardBody card={CARDS[1]} light />
          </CardShell>
        </Reveal>

        {/* Row 2 */}
        {CARDS.slice(2).map((card, i) => (
          <Reveal key={card.id} className="md:col-span-2" delay={0.08 + i * 0.06}>
            <CardShell card={card}>
              <CardBody card={card} />
            </CardShell>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Services;
